import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Database, Users, FileText, CheckCircle2, Clock, Loader2 } from "lucide-react";

type AnalysisStage = 
  | 'initializing'
  | 'finding_competitors'
  | 'scraping_profiles'
  | 'analyzing_employees'
  | 'detecting_ads'
  | 'generating_report'
  | 'completed';

interface AuditProgressDialogProps {
  submissionId: string;
  open: boolean;
  onClose: () => void;
}

export function AuditProgressDialog({ submissionId, open, onClose }: AuditProgressDialogProps) {
  const [status, setStatus] = useState<string>('initializing');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [startTime] = useState(Date.now());
  const [showTimeout, setShowTimeout] = useState(false);

  useEffect(() => {
    if (!open || !submissionId) return;

    const pollStatus = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('get-audit-status', {
          body: { id: submissionId }
        });

        if (error) throw error;

        if (data?.data) {
          const elapsedTime = Date.now() - startTime;
          
          setStatus(data.data.status);
          
          // Calculate progress based on status
          const statusProgress: Record<string, number> = {
            'initializing': 10,
            'finding_competitors': 25,
            'scraping_profiles': 45,
            'analyzing_employees': 65,
            'detecting_ads': 85,
            'generating_report': 95,
            'completed': 100
          };
          
          setProgress(statusProgress[data.data.status] || 0);

          // If stuck in 'processing' for over 5 minutes, show timeout message
          if (data.data.status === 'processing' && elapsedTime > 5 * 60 * 1000) {
            setShowTimeout(true);
            if (data.data.report_data) {
              // Has report data, can close
              console.log('⚠️ Audit stuck but has report_data, closing dialog...');
              setTimeout(() => onClose(), 500);
            }
          }

          if (data.data.status === 'completed') {
            setTimeout(() => {
              onClose();
            }, 1500);
          }

          if (data.data.status === 'failed') {
            setError('Wystąpił błąd podczas audytu');
          }
        }
      } catch (err) {
        console.error('Error polling status:', err);
        setError('Błąd podczas sprawdzania statusu');
      }
    };

    // Initial poll
    pollStatus();

    // Poll every 3 seconds
    const interval = setInterval(pollStatus, 3000);

    return () => clearInterval(interval);
  }, [submissionId, open, onClose]);

  const stages = [
    { id: 'finding_competitors', label: 'Wyszukiwanie konkurencji', icon: Database },
    { id: 'scraping_profiles', label: 'Pobieranie profili', icon: Users },
    { id: 'analyzing_employees', label: 'Analiza pracowników', icon: Users },
    { id: 'detecting_ads', label: 'Wykrywanie reklam', icon: FileText },
    { id: 'generating_report', label: 'Generowanie raportu', icon: FileText },
  ];

  const getStageStatus = (stageId: string) => {
    const stageOrder = ['finding_competitors', 'scraping_profiles', 'analyzing_employees', 'detecting_ads', 'generating_report'];
    const currentIndex = stageOrder.indexOf(status);
    const stageIndex = stageOrder.indexOf(stageId);

    if (currentIndex > stageIndex) return 'completed';
    if (currentIndex === stageIndex) return 'active';
    return 'pending';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] md:max-w-2xl p-4 md:p-6">
        <DialogHeader>
          <DialogTitle>Przebieg audytu Social Selling</DialogTitle>
        </DialogHeader>

        {error ? (
          <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
            {error}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overall Progress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Całkowity postęp</span>
                <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-3" />
            </div>

            {/* Stages */}
            <div className="space-y-4">
              {stages.map((stage, index) => {
                const stageStatus = getStageStatus(stage.id);
                const Icon = stage.icon;

                return (
                  <div
                    key={stage.id}
                    className={`flex items-start gap-4 p-4 rounded-lg border transition-all ${
                      stageStatus === 'active'
                        ? 'border-primary bg-primary/5'
                        : stageStatus === 'completed'
                        ? 'border-green-500/50 bg-green-500/5'
                        : 'border-muted bg-muted/20'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {stageStatus === 'completed' ? (
                        <CheckCircle2 className="h-6 w-6 text-green-500" />
                      ) : stageStatus === 'active' ? (
                        <Loader2 className="h-6 w-6 text-primary animate-spin" />
                      ) : (
                        <Icon className="h-6 w-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold">{stage.label}</h4>
                        {stageStatus === 'active' && (
                          <Badge variant="outline" className="bg-primary/10">
                            W trakcie
                          </Badge>
                        )}
                        {stageStatus === 'completed' && (
                          <Badge variant="outline" className="bg-green-500/10 text-green-700">
                            Ukończono
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {status === 'completed' && (
              <div className="text-center p-4 bg-green-500/10 rounded-lg">
                <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                <p className="font-semibold text-green-700">Audyt zakończony!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Przekierowanie do raportu...
                </p>
              </div>
            )}

            {status === 'processing' && !showTimeout && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>Szacowany czas: 2-3 minuty</span>
              </div>
            )}

            {showTimeout && (
              <div className="text-center p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
                <p className="text-sm text-orange-700 dark:text-orange-400">
                  Audyt trwa dłużej niż zwykle. Możesz zamknąć to okno i sprawdzić panel admina.
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
